import { useEffect, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from "chart.js";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import zoomPlugin from "chartjs-plugin-zoom";
import { useTranslation } from "react-i18next";
import fi from "date-fns/locale/fi";
import enUS from "date-fns/locale/en-US";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  zoomPlugin
);

// Chart options
const options = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      type: "time",
      time: {
        unit: "day",
        displayFormats: {
          day: "MMM d, yyyy",
        },
        tooltipFormat: "MMM d, yyyy HH:mm",
      },
      title: {
        display: true,
        text: "Date",
      },
    },
    y: {
      type: "linear",
      display: true,
      position: "left",
      title: {
        display: true,
        text: "Average (30)",
      },
      sync: true,
    },
    y1: {
      type: "linear",
      display: true,
      position: "right",
      title: {
        display: true,
        text: "Max limit",
      },
      grid: {
        drawOnChartArea: false,
      },
      sync: true,
    },
  },
  plugins: {
    legend: {
      position: "top",
    },
    title: {
      display: true,
      text: "Water consumption",
    },
    zoom: {
      pan: {
        enabled: true,
        mode: "xy",
      },
      zoom: {
        wheel: {
          enabled: true,
        },
        pinch: {
          enabled: true,
        },
        mode: "xy",
        drag: {
          enabled: true,
        },
      },
    },
  },
};

const Details = () => {
  const { guid } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const chartRef = useRef(null);
  const { startDate, endDate } = location.state || {};
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t, i18n } = useTranslation();

  const parseDate = (dateString) => {
    const cleanDateString = dateString.replace(" +00:00", "");
    return new Date(cleanDateString);
  };

  const prepareChartData = (dataPoints, t) => {
    // Find the overall min and max values across both datasets
    const allValues = dataPoints.flatMap((point) => [
      point.Aver30,
      point.Limit,
    ]);
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    // Add some padding to the min/max for better visualization
    const padding = (max - min) * 0.1;

    // Update the options object with the calculated min/max
    options.scales.y.min = min - padding;
    options.scales.y.max = max + padding;
    options.scales.y1.min = min - padding;
    options.scales.y1.max = max + padding;

    // Create segment colors based on whether Aver30 exceeds Limit
    const segments = dataPoints.map((point, index) => ({
      from: point.Date,
      to:
        index < dataPoints.length - 1 ? dataPoints[index + 1].Date : point.Date,
      color:
        point.Aver30 > point.Limit ? "rgb(255, 0, 0)" : "rgb(75, 192, 192)", // Red when exceeding limit
      backgroundColor:
        point.Aver30 > point.Limit
          ? "rgba(255, 0, 0, 0.2)"
          : "rgba(75, 192, 192, 0.2)",
    }));
    return {
      datasets: [
        {
          label: t("average_30min"),
          data: dataPoints.map((point) => ({
            x: point.Date,
            y: point.Aver30,
          })),
          segment: {
            borderColor: (ctx) => segments[ctx.p0DataIndex].color,
          },
          backgroundColor: (ctx) =>
            ctx.raw ? segments[ctx.dataIndex].backgroundColor : undefined,

          borderWidth: 1,
          yAxisID: "y",
        },
        {
          label: t("water_consumption_threshold"),
          data: dataPoints.map((point) => ({
            x: point.Date,
            y: point.Limit,
          })),
          borderColor: "rgb(255, 99, 132)",
          backgroundColor: "rgba(255, 99, 132, 0.2)",
          borderWidth: 1,
          yAxisID: "y1",
        },
      ],
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!guid || !startDate || !endDate) {
        console.log("Missing parameters:", { guid, startDate, endDate });
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(
          "https://vesimittari.azurewebsites.net/api/rajapinta",
          {
            method: "POST",
            mode: "cors",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "x-function-key":
                "9EoYG3kKp5Jl9eYd-piUNqCaZKhRhOaXc0xvvXItDIPAzFu0KtE-A==",
            },
            body: JSON.stringify({
              c: "getdatarange",
              p: [guid, "0", startDate, endDate],
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }

        const result = await response.json();
        console.log("API Response:", result);

        if (result.t && result.t.length > 0) {
          const parsedData = JSON.parse(result.t);
          console.log("Parsed Data:", parsedData);

          if (parsedData.DataPointList && parsedData.DataPointList.length > 0) {
            const chartData = parsedData.DataPointList.map((point) => ({
              Aver30: point.Aver30 || 0,
              Limit: point.Limit || 0,
              Date: parseDate(point.Date),
            }));

            chartData.sort((a, b) => a.Date - b.Date);
            console.log("Processed Chart Data:", chartData);

            const prepared = prepareChartData(chartData, t);
            console.log("Final Chart Data:", prepared);
            setChartData(prepared);
          } else {
            setError("No data points available");
          }
        } else {
          setError("No data available");
        }
      } catch (err) {
        console.error("Error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [guid, startDate, endDate, t]);

  const handleReset = () => {
    if (chartRef.current) {
      chartRef.current.resetZoom();
    }
  };

  if (!guid || !startDate || !endDate) {
    return (
      <div className="p-4">
        <div className="text-red-600">Missing required parameters</div>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
        >
          ← Back
        </button>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-blue-500 text-white rounded"
        >
          ← Back
        </button>
        {chartData && (
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-green-500 text-white rounded"
          >
            Reset Zoom
          </button>
        )}
      </div>

      {loading && <div className="text-center py-4">Loading data...</div>}

      {error && (
        <div className="text-center py-4 text-red-600">Error: {error}</div>
      )}

      {chartData && (
        <div
          className="bg-white rounded-lg shadow-lg p-4 md:p-6 w-full"
          style={{
            width: "100%",
            minHeight: "300px",
            maxWidth: "1200px",
            margin: "0 auto",
            height: "60vh",
            overflowX: "hidden",
            overflowY: "hidden",
          }}
        >
          <Line
            ref={chartRef}
            data={chartData}
            options={{
              ...options,
              adapters: {
                date: { locale: i18n.language === "fi" ? fi : enUS },
              },
              maintainAspectRatio: false, // Make sure this is false
              responsive: true,
              plugins: {
                ...options.plugins,
                legend: {
                  ...options.plugins.legend,
                  display: true,
                  position: "top",
                  labels: {
                    boxWidth: 40,
                    padding: 10,
                    font: {
                      size: window.innerWidth < 768 ? 10 : 12, // Smaller font on mobile
                    },
                  },
                },
                title: {
                  ...options.plugins.title,
                  text: t("water_consumption"),
                },
              },
              scales: {
                ...options.scales,
                x: {
                  ...options.scales.x,
                  title: { ...options.scales.x.title, text: t("date") },
                },
                y: {
                  ...options.scales.y,
                  title: {
                    ...options.scales.y.title,
                    text: t("average_30min"),
                  },
                },
                y1: {
                  ...options.scales.y1,
                  title: { ...options.scales.y1.title, text: t("max_limit") },
                },
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Details;
